import React, { useCallback, useEffect, useRef, useState } from 'react';
import XMarkIcon from './icons/XMarkIcon';
import CameraIcon from './icons/CameraIcon';
import VideoCameraIcon from './icons/VideoCameraIcon';
import { canListLibrary, listRecents, resolveAsset, type LibraryAsset } from '../utils/mediaLibrary';

export type CreateMode = 'post' | 'zip' | 'news';

interface Entry {
  key: string;
  thumb: string;
  isVideo: boolean;
  duration?: number;
  file?: File;
  asset?: LibraryAsset;
}

interface CreateMediaPageProps {
  onClose: () => void;
  onNext: (mode: CreateMode, files: File[]) => void;
}

const MODES: { id: CreateMode; label: string }[] = [
  { id: 'post', label: 'Post' },
  { id: 'zip', label: 'Zip' },
  { id: 'news', label: 'News' },
];
const MAX_POST_MEDIA = 10;

const MODE_INFO: Record<CreateMode, string> = {
  post: `Choose up to ${MAX_POST_MEDIA} photos or videos`,
  zip: 'Choose one video · up to 3 minutes',
  news: 'Choose one photo or video · disappears in 24 hours',
};

const MODE_TITLE: Record<CreateMode, string> = { post: 'Create post', zip: 'Create Zip', news: 'Create news' };

const fmtBytes = (b: number) => (b >= 1024 * 1024 ? `${(b / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const fmtDuration = (s?: number) => (s ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}` : '');

const CreateMediaPage: React.FC<CreateMediaPageProps> = ({ onClose, onNext }) => {
  const [mode, setMode] = useState<CreateMode>('post');
  const [entries, setEntries] = useState<Entry[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [dims, setDims] = useState<Record<string, string>>({});
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const [previewSrc, setPreviewSrc] = useState<Record<string, string>>({});
  const [loadingLib, setLoadingLib] = useState(canListLibrary());
  const [libError, setLibError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showSourceMenu, setShowSourceMenu] = useState(false);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dragDx, setDragDx] = useState(0);
  const stripRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ key: string; startX: number; pointerId: number; touch: boolean; active: boolean; timer: number | null } | null>(null);
  const dragActiveRef = useRef(false);
  const pickRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const resolved = useRef<Map<string, File>>(new Map());
  const urlsRef = useRef<string[]>([]);

  const singleOnly = mode !== 'post';

  useEffect(() => {
    if (!canListLibrary()) return;
    let cancelled = false;
    listRecents()
      .then((assets) => {
        if (cancelled) return;
        const list = assets.map((a) => ({ key: a.id, thumb: a.thumb, isVideo: a.isVideo, duration: a.duration, asset: a }));
        setEntries(list);
      })
      .catch((e) => !cancelled && setLibError(e?.message?.includes('ermission') ? 'Allow photo access in Settings to see your camera roll.' : 'Could not load your camera roll.'))
      .finally(() => !cancelled && setLoadingLib(false));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => () => urlsRef.current.forEach((u) => URL.revokeObjectURL(u)), []);

  const activeKey = focusKey && selected.includes(focusKey) ? focusKey : selected[selected.length - 1];
  const lightboxEntry = entries.find((e) => e.key === activeKey) || null;

  // Full-resolution preview for the focused camera-roll asset.
  useEffect(() => {
    const asset = lightboxEntry?.asset;
    if (!lightboxEntry || !asset || previewSrc[lightboxEntry.key]) return;
    const key = lightboxEntry.key;
    let cancelled = false;
    resolveAsset(asset)
      .then((file) => {
        resolved.current.set(key, file);
        const url = URL.createObjectURL(file);
        urlsRef.current.push(url);
        if (!cancelled) setPreviewSrc((p) => ({ ...p, [key]: url }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [lightboxEntry, previewSrc]);

  const addFiles = useCallback((files: File[]) => {
    const fresh: Entry[] = files
      .filter((f) => f.type.startsWith('image/') || f.type.startsWith('video/'))
      .map((file) => {
        const url = URL.createObjectURL(file);
        urlsRef.current.push(url);
        const key = `f_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
        resolved.current.set(key, file);
        setPreviewSrc((p) => ({ ...p, [key]: url }));
        return { key, thumb: url, isVideo: file.type.startsWith('video/'), file };
      });
    if (!fresh.length) { setError('Please choose photos or videos.'); return; }
    setError('');
    setEntries((prev) => [...fresh, ...prev]);
    setSelected((prev) => (singleOnly ? [fresh[0].key] : [...prev, ...fresh.map((f) => f.key)].slice(0, MAX_POST_MEDIA)));
  }, [singleOnly]);

  const toggle = (key: string) => {
    setError('');
    // Tapping a selected tile first brings it into the preview; tapping it again deselects it.
    if (selected.includes(key) && activeKey !== key) { setFocusKey(key); return; }
    setFocusKey(key);
    setSelected((prev) => {
      if (singleOnly) return prev[0] === key ? [] : [key];
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= MAX_POST_MEDIA) { setError(`Posts can have up to ${MAX_POST_MEDIA} items.`); return prev; }
      return [...prev, key];
    });
  };

  const switchMode = (m: CreateMode) => {
    setMode(m);
    setError('');
    if (m !== 'post') setSelected((prev) => (prev.length ? [prev[prev.length - 1]] : prev));
  };

  const handleNext = async () => {
    if (!selected.length || busy) return;
    setBusy(true);
    setError('');
    try {
      const files: File[] = [];
      for (const key of selected) {
        let f = resolved.current.get(key);
        if (!f) {
          const entry = entries.find((e) => e.key === key);
          if (entry?.asset) { f = await resolveAsset(entry.asset); resolved.current.set(key, f); }
        }
        if (f) files.push(f);
      }
      if (!files.length) throw new Error('Could not read the selected media.');
      if (mode === 'zip' && !files[0].type.startsWith('video/')) throw new Error('Zips only support video. Pick a video.');
      onNext(mode, files);
    } catch (e: any) {
      setError(e?.message || 'Could not open that media.');
    } finally {
      setBusy(false);
    }
  };

  // --- Drag selected thumbnails to reorder (mouse: drag; touch: press-and-hold, then drag) ---
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const stop = (e: TouchEvent) => { if (dragActiveRef.current && e.cancelable) e.preventDefault(); };
    el.addEventListener('touchmove', stop, { passive: false });
    return () => el.removeEventListener('touchmove', stop);
  }, []);

  const beginDrag = (key: string) => {
    const d = dragRef.current;
    if (!d || d.active) return;
    d.active = true;
    dragActiveRef.current = true;
    setDragKey(key);
    setDragDx(0);
    try { stripRef.current?.querySelector<HTMLElement>(`[data-key="${key}"]`)?.setPointerCapture(d.pointerId); } catch { /* ignore */ }
  };

  const onThumbPointerDown = (e: React.PointerEvent, key: string) => {
    if ((e.target as HTMLElement).closest('[data-remove]')) return;
    const touch = e.pointerType === 'touch';
    const d = { key, startX: e.clientX, pointerId: e.pointerId, touch, active: false, timer: null as number | null };
    dragRef.current = d;
    if (touch) d.timer = window.setTimeout(() => beginDrag(key), 280);
  };

  const onThumbPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const moved = e.clientX - d.startX;
    if (!d.active) {
      if (d.touch) { if (Math.abs(moved) > 10 && d.timer) { window.clearTimeout(d.timer); dragRef.current = null; } return; }
      if (Math.abs(moved) < 6) return;
      beginDrag(d.key);
    }
    const items: HTMLElement[] = Array.from(stripRef.current?.querySelectorAll<HTMLElement>('[data-key]') ?? []);
    const from = items.findIndex((n) => n.dataset.key === d.key);
    if (from === -1) return;
    const step = items.length > 1 ? items[1].getBoundingClientRect().left - items[0].getBoundingClientRect().left : 0;
    setDragDx(moved);
    if (step > 0) {
      const target = Math.max(0, Math.min(items.length - 1, from + Math.round(moved / step)));
      if (target !== from) {
        setSelected((prev) => {
          const next = prev.slice();
          const [m] = next.splice(from, 1);
          next.splice(target, 0, m);
          return next;
        });
        d.startX += (target - from) * step;
        setDragDx(e.clientX - d.startX);
      }
    }
  };

  const endDrag = () => {
    const d = dragRef.current;
    if (d?.timer) window.clearTimeout(d.timer);
    dragRef.current = null;
    dragActiveRef.current = false;
    setDragKey(null);
    setDragDx(0);
  };

  const removeSelected = (key: string) => {
    setError('');
    setSelected((prev) => prev.filter((k) => k !== key));
    setFocusKey((f) => (f === key ? null : f));
  };

  const hasLib = canListLibrary();

  const renderThumb = (e: Entry, cls: string) =>
    e.isVideo && !e.asset
      ? <video src={`${e.thumb}#t=0.001`} className={cls} muted playsInline preload="metadata" />
      : <img src={e.thumb} alt="" className={cls} loading="lazy" />;

  const focus = lightboxEntry;
  const focusFile = focus ? (focus.file || resolved.current.get(focus.key)) : null;
  const info = focus
    ? [focus.isVideo ? 'Video' : 'Photo', focus.isVideo && focus.duration ? fmtDuration(focus.duration) : null, dims[focus.key], focusFile ? fmtBytes(focusFile.size) : null].filter(Boolean) as string[]
    : [];

  const nextLabel = busy ? 'Opening…' : 'Next';

  return (
    <div className="ridaz-fullpage ridaz-create fixed inset-0 z-[6000] flex flex-col bg-[#0b0c0e] text-white">
      <input ref={pickRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => { const f: File[] = Array.from(e.target.files ?? []); e.target.value = ''; if (f.length) addFiles(f); }} />
      <input ref={cameraRef} type="file" accept="image/*,video/*" capture="environment" className="hidden" onChange={(e) => { const f: File[] = Array.from(e.target.files ?? []); e.target.value = ''; if (f.length) addFiles(f); }} />

      {/* Header */}
      <div className="grid shrink-0 grid-cols-[auto_1fr_auto] items-center gap-3 px-4 pb-3" style={{ paddingTop: 'calc(var(--sat-native, env(safe-area-inset-top)) + 0.75rem)' }}>
        <button onClick={onClose} className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] transition active:scale-95" aria-label="Close"><XMarkIcon className="h-6 w-6" /></button>
        <h1 className="text-center text-2xl font-black tracking-tight">{MODE_TITLE[mode]}</h1>
        <span className="px-1 text-base font-bold text-white/35" aria-disabled="true">Drafts</span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="px-4">
          {/* Post / Zip / News */}
          <div role="tablist" aria-label="Create type" className="grid grid-cols-3 rounded-2xl border border-white/10 bg-white/[0.04] p-1">
            {MODES.map((m) => (
              <button
                key={m.id}
                role="tab"
                aria-selected={mode === m.id}
                onClick={() => switchMode(m.id)}
                className={`rounded-xl py-2.5 text-[17px] font-extrabold transition ${mode === m.id ? 'bg-lime-400 text-black' : 'text-white/55 hover:text-white'}`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {/* Large preview */}
          <div className="relative mt-3 aspect-[1.6/1] w-full overflow-hidden rounded-2xl bg-[#121316]">
            {focus ? (
              focus.isVideo
                ? <video key={focus.key} src={previewSrc[focus.key]} poster={focus.asset ? focus.thumb : undefined} className="h-full w-full object-cover" autoPlay muted loop playsInline onLoadedMetadata={(ev) => { const v = `${ev.currentTarget.videoWidth}×${ev.currentTarget.videoHeight}`; setDims((d) => ({ ...d, [focus.key]: v })); }} />
                : <img src={previewSrc[focus.key] || focus.thumb} alt="Preview" className="h-full w-full object-cover" onLoad={(ev) => { const v = `${ev.currentTarget.naturalWidth}×${ev.currentTarget.naturalHeight}`; if (previewSrc[focus.key] || !focus.asset) setDims((d) => ({ ...d, [focus.key]: v })); }} />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-2 px-10 text-center">
                <CameraIcon className="h-9 w-9 text-white/25" />
                <p className="text-sm font-semibold text-white/40">{loadingLib ? 'Loading your camera roll…' : 'Pick something below to preview it here'}</p>
              </div>
            )}
            {focus && selected.length > 0 && (
              <span className="pointer-events-none absolute bottom-3 left-3 rounded-full bg-black/65 px-3.5 py-1.5 text-sm font-extrabold tabular-nums backdrop-blur">
                {Math.max(1, selected.indexOf(activeKey) + 1)} / {selected.length}
              </span>
            )}
          </div>

          {/* Selected thumbnails (drag to reorder) */}
          {(selected.length > 0 || mode === 'post') && (
            <div ref={stripRef} className="mt-3 flex gap-2.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {selected.map((key, i) => {
                const e = entries.find((x) => x.key === key);
                if (!e) return null;
                const dragging = dragKey === key;
                return (
                  <div
                    key={key}
                    data-key={key}
                    onPointerDown={(ev) => onThumbPointerDown(ev, key)}
                    onPointerMove={onThumbPointerMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onClick={() => { if (!dragActiveRef.current) setFocusKey(key); }}
                    className={`relative aspect-[1.45/1] w-[calc((100%-30px)/4)] shrink-0 select-none overflow-hidden rounded-xl border-2 ${key === activeKey ? 'border-lime-400' : 'border-lime-400/60'} ${dragging ? 'z-10 scale-105 shadow-2xl shadow-black/60' : ''} ${singleOnly ? '' : 'cursor-grab'}`}
                    style={{ transform: dragging ? `translateX(${dragDx}px) scale(1.05)` : undefined, transition: dragging ? 'none' : 'transform 0.15s ease', touchAction: 'pan-x' }}
                  >
                    {renderThumb(e, 'pointer-events-none h-full w-full object-cover')}
                    <span className="absolute left-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-lime-400 text-xs font-black text-black">{i + 1}</span>
                    <button
                      data-remove
                      type="button"
                      onClick={(ev) => { ev.stopPropagation(); removeSelected(key); }}
                      className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/65 text-white"
                      aria-label="Remove"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
              {!singleOnly && selected.length < MAX_POST_MEDIA && (
                <button
                  type="button"
                  onClick={() => (hasLib ? cameraRef.current?.click() : pickRef.current?.click())}
                  className="flex aspect-[1.45/1] w-[calc((100%-30px)/4)] shrink-0 items-center justify-center rounded-xl border-2 border-dashed border-white/20 text-white/50 transition active:scale-95"
                  aria-label="Add more"
                >
                  <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                </button>
              )}
            </div>
          )}
          {mode === 'post' && !singleOnly && selected.length > 0 && (
            <p className="mt-2.5 rounded-xl bg-white/[0.06] px-3.5 py-2.5 text-[13px] font-semibold leading-snug text-white/70">
              The first photo or video sets the size of your post. Anything you add after it is cropped to match, so every slide stays the same size.
            </p>
          )}
        </div>

        <div className="mx-4 mt-3 border-t border-white/10" />

        {/* Source + Camera */}
        <div className="relative flex items-center justify-between px-4 pt-3">
          <button
            type="button"
            onClick={() => setShowSourceMenu((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={showSourceMenu}
            className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-5 py-3 text-base font-extrabold"
          >
            Recents
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
          </button>
          <button onClick={() => cameraRef.current?.click()} className="flex items-center gap-2.5 rounded-xl border border-white/20 px-5 py-3 text-base font-extrabold transition active:scale-95" aria-label="Open camera">
            <CameraIcon className="h-6 w-6" />
            Camera
          </button>
          {showSourceMenu && (
            <div role="menu" className="absolute left-4 top-[calc(100%-0.25rem)] z-20 w-56 overflow-hidden rounded-xl border border-white/10 bg-[#1b1c20] shadow-2xl">
              <button role="menuitem" onClick={() => setShowSourceMenu(false)} className="block w-full px-4 py-3 text-left text-sm font-bold text-lime-300 hover:bg-white/5">Recents</button>
              <button role="menuitem" onClick={() => { setShowSourceMenu(false); pickRef.current?.click(); }} className="block w-full border-t border-white/5 px-4 py-3 text-left text-sm font-bold hover:bg-white/5">Choose from files…</button>
            </div>
          )}
        </div>

        <p className="px-4 pb-2 pt-3 text-[15px] font-medium text-white/45">{MODE_INFO[mode]}</p>

        {error && <p role="alert" className="mx-4 mb-2 rounded-xl bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-200">{error}</p>}
        {libError && <p className="mx-4 mb-2 text-xs font-semibold text-gray-300">{libError}</p>}

        {/* Photo / video grid */}
        <div className="grid grid-cols-4 gap-2 px-4 pb-4">
          {entries.map((e) => {
            const idx = selected.indexOf(e.key);
            const isSel = idx !== -1;
            return (
              <button
                key={e.key}
                onClick={() => toggle(e.key)}
                className={`relative aspect-[0.92/1] overflow-hidden rounded-xl bg-white/5 transition active:scale-95 ${isSel ? 'border-2 border-lime-400' : 'border-2 border-transparent'}`}
                aria-label={e.isVideo ? 'Video' : 'Photo'}
                aria-pressed={isSel}
              >
                {renderThumb(e, 'h-full w-full object-cover')}
                {e.isVideo && (
                  <span className="absolute bottom-1.5 left-1.5 flex items-center gap-1.5 rounded-lg bg-black/65 px-2 py-1 text-xs font-extrabold backdrop-blur">
                    <VideoCameraIcon className="h-4 w-4" />
                    {fmtDuration(e.duration) || ''}
                  </span>
                )}
                <span className={`absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full text-xs font-black ${isSel ? 'bg-lime-400 text-black' : 'border-2 border-white/80 bg-black/25'}`}>{isSel ? (singleOnly ? '✓' : idx + 1) : ''}</span>
              </button>
            );
          })}
        </div>
        {!entries.length && !loadingLib && !hasLib && (
          <div className="px-4 pb-8 text-center">
            <p className="text-sm font-semibold text-gray-300">Nothing picked yet</p>
            <p className="mt-1 text-xs text-gray-500">Use the camera, or choose photos and videos from your device.</p>
            <button onClick={() => pickRef.current?.click()} className="mt-4 rounded-2xl bg-lime-400 px-5 py-2.5 text-sm font-black text-black transition active:scale-95">Choose from library</button>
          </div>
        )}
      </div>

      {/* Bottom bar */}
      <div className="flex shrink-0 items-center justify-between gap-3 border-t border-white/10 bg-[#0b0c0e] px-5 pt-3" style={{ paddingBottom: 'calc(var(--sab-native, env(safe-area-inset-bottom)) + 0.75rem)' }}>
        <div className="min-w-0">
          <p className="text-2xl font-black leading-tight">{selected.length} selected</p>
          <p className="text-[13px] font-medium leading-tight text-white/45">{!singleOnly ? 'Drag thumbnails to reorder' : 'Tap to change your pick'}</p>
        </div>
        <button
          onClick={handleNext}
          disabled={!selected.length || busy}
          className="flex h-14 shrink-0 items-center gap-2.5 rounded-2xl bg-lime-400 px-8 text-xl font-black text-black transition active:scale-95 disabled:bg-white/15 disabled:text-white/40"
          aria-label="Continue"
        >
          {nextLabel}
          <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
        </button>
      </div>
    </div>
  );
};

export default CreateMediaPage;
